import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';
import { Icons, Link, Table, TableRow, Icon, IconSizes, Flexbox, useTooltip } from '@skf-internal/ui-components-react-legacy';
import { GetEntityUrl, GetTraslationIdForEntityCategoryPlural } from 'utils';
import { RelationTurbineNullable, RelationGearbox, RelationGenerator } from 'domain/index';
import _ from 'lodash';
import { fromTurbineId, fromGearboxId } from '../../../../../domain/shared/ObjectId';
import { EntityState, stateToColor, stateToStyle } from '../../entity-state';
import { useEffect, useRef } from 'react';

const GeneratorTable = ({ generators, editMode, onEditGenerator, onDeleteGenerator }: Props) => {
  const intl = useIntl();

  const tableHead: TableRow[] = [
    {
      cells: [
        { children: '', scope: 'col' },
        { children: intl.formatMessage({ id: 'windTurbine.name' }), scope: 'col' },
        { children: intl.formatMessage({ id: 'windTurbine.manufacturer' }), scope: 'col' },
        { children: intl.formatMessage({ id: 'windTurbine.marketShare' }), scope: 'col' },
        { children: intl.formatMessage({ id: 'windTurbine.remark' }), scope: 'col' },
        { children: '', scope: 'col' }
      ]
    }
  ];

  const tdStyle = 'align-top whitespace-normal';

  const tableBody: TableRow[] = generators.map((generator, index) => ({
    cells: [
      { width: '1%', className: stateToStyle(tdStyle, generator.metadata?.state), children: <div title={'status'} className={`w-3 h-10 ${stateToColor(generator.metadata?.state)}`}></div> },
      {
        width: '20%',
        className: stateToStyle(tdStyle, generator.metadata?.state),
        children: <GeneratorNameCell generator={generator} editMode={editMode} />
      },
      { width: '20%', className: stateToStyle(tdStyle, generator.metadata?.state), children: <p>{generator.generator?.manufacturer?.manufacturerName ?? ''}</p> },
      { width: '10%', className: stateToStyle(tdStyle, generator.metadata?.state), children: <p className="text-right">{generator.marketShare}</p> },
      { width: '45%', className: stateToStyle(tdStyle, generator.metadata?.state), children: <p>{generator.remark}</p> },
      {
        width: '5%',
        className: 'align-top',
        children: (
          <>
            {editMode && generator.metadata?.state !== EntityState.DELETED && generator.metadata?.state !== EntityState.DELETED_UNSAVED && (
              <>
                <Link
                  as="button"
                  aria-label={intl.formatMessage({ id: 'windTurbine.button.edit' })}
                  feIcon={{ feIcon: Icons.Edit, position: 'left' }}
                  children=""
                  onClick={() => {
                    onEditGenerator(generator, index);
                  }}
                />
                <Link
                  as="button"
                  aria-label={intl.formatMessage({ id: 'windTurbine.button.delete' })}
                  feIcon={{ feIcon: Icons.Trash, position: 'left' }}
                  children=""
                  onClick={() => {
                    onDeleteGenerator(
                      generators.find((x) => x.objectId === generator.objectId),
                      index
                    );
                  }}
                />
              </>
            )}
          </>
        )
      }
    ]
  }));

  return (
    <>
      {_.some(generators) ? (
        <Table className="mt-5 mb-5" feBody={tableBody} feHead={tableHead} />
      ) : (
        <p className="mb-5">{`${intl.formatMessage({ id: 'windTurbine.empty.table' })} ${intl.formatMessage({ id: GetTraslationIdForEntityCategoryPlural('Generator') })}`}</p>
      )}
    </>
  );
};

interface Props {
  generators: RelationGenerator[];
  editMode: boolean;
  onEditGenerator: any;
  onDeleteGenerator: any;
}

const GeneratorNameCell = ({ generator, editMode }: { generator: RelationGenerator; editMode: boolean }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { changesetId } = useParams();
  const { addPopover } = useTooltip();
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const handleMouseOver = () => {
    addPopover({ content: intl.formatMessage({ id: 'windTurbine.missing.tooltip' }), feType: 'tooltip' }, tooltipRef.current);
  };

  if (!generator.generator) {
    return (
      <Flexbox className="cursor-pointer" onMouseOver={handleMouseOver} ref={tooltipRef}>
        {intl.formatMessage({ id: 'windTurbine.missing' }, { id: generator.objectId })}
        <Icon className="ml-2" aria-label="Information" feIcon={Icons.InfoCircleOutlined} feSize={IconSizes.Lg} title="Test" />
      </Flexbox>
    );
  } else if (editMode) {
    return <p>{generator.generator?.generatorName ?? ''}</p>;
  } else {
    return <Link as="a" href={GetEntityUrl(fromGearboxId(generator.objectId), changesetId)} onClick={(_, route) => navigate(route)}>{`${generator.generator.generatorName}`}</Link>;
  }
};

export default GeneratorTable;
